import { useContext, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import FeedCard from "../../components/FeedCard/FeedCard";
import Skeleton from "../../components/Skeleton/Skeleton";
import styles from "./Home.module.css";
import BottomNav from "../../components/BottomNav";
import api from "../../services/api";
import PullToRefresh from 'react-simple-pull-to-refresh';

// Data efetiva de um comunicado pra fins de ORDENAÇÃO do feed - mesma regra
// usada pra EXIBIÇÃO em FeedCard.jsx/dataExibidaFeed (pedido do usuário,
// 2026-10-20): agendado usa datcom+horcom, senão datger (criação). O backend
// (GetComunicadoUser) ordena só por datger - reordena aqui no front porque
// misturar comparação de datetime (datger) com string dd/mm/yyyy (datcom) em
// SQL é mais arriscado do que resolver com Date já no cliente.
function dataEfetivaComunicado(post) {
    if (post.tipcom === 'agendado' && post.datcom) {
        const [d, m, y] = post.datcom.split('/');
        const [hh, mm] = (post.horcom || '00:00').split(':');
        return new Date(y, m - 1, d, hh || 0, mm || 0).getTime();
    }
    const datger = post.datger || '';
    const br = datger.match(/^(\d{2})\/(\d{2})\/(\d{4})[ T]?(\d{2}:\d{2}(:\d{2})?)?/);
    if (br) {
        const [, d, m, y, hms] = br;
        const dt = new Date(`${y}-${m}-${d}T${hms || '00:00:00'}`);
        return isNaN(dt) ? 0 : dt.getTime();
    }
    const dt = new Date(datger.replace(' ', 'T'));
    return isNaN(dt) ? 0 : dt.getTime();
}

const LIMITE_LIDOS = 10;
const CACHE_TTL = 3 * 60 * 1000; // 3 minutos

// cache em memória — sobrevive a remounts sem depender de storage
const _cache = { ts: 0, feed: null, todosLidos: null };

function lerCache() {
    if (!_cache.feed || Date.now() - _cache.ts > CACHE_TTL) return null;
    return { feed: _cache.feed, todosLidos: _cache.todosLidos };
}

function salvarCache(feed, todosLidos) {
    _cache.ts = Date.now();
    _cache.feed = feed;
    _cache.todosLidos = todosLidos;
}

function limparCache() {
    _cache.ts = 0;
    _cache.feed = null;
    _cache.todosLidos = null;
}

export default function Home() {
    const { user } = useContext(AuthContext);
    const [loading, setLoading] = useState(true);
    const [foto] = useState(() => localStorage.getItem("fotoFuncionario"));

    const [feed, setFeed] = useState([]);
    const [todosLidos, setTodosLidos] = useState([]);
    const [limiteLidos, setLimiteLidos] = useState(LIMITE_LIDOS);

    const greetingRef = useRef(null);
    const [greetingOpacity, setGreetingOpacity] = useState(1);

    useEffect(() => {
        const LAYOUT_HEADER_H = 52;
        const handleScroll = () => {
            if (!greetingRef.current) return;
            const rect = greetingRef.current.getBoundingClientRect();
            const dist = rect.bottom - LAYOUT_HEADER_H;
            const opacity = Math.max(0, Math.min(1, dist / rect.height));
            setGreetingOpacity(opacity);
        };
        // captura scroll de qualquer container, incluindo o .content do LayoutBase
        document.addEventListener('scroll', handleScroll, { passive: true, capture: true });
        return () => document.removeEventListener('scroll', handleScroll, { capture: true });
    }, []);

    async function CarregarComunicados(limiteAtual = LIMITE_LIDOS, forcarReload = false, silencioso = false) {
        if (!forcarReload) {
            const cache = lerCache();
            if (cache) {
                setFeed(cache.feed);
                setTodosLidos(cache.todosLidos);
                setLoading(false);
                return;
            }
        }

        try {
            if (!silencioso) setLoading(true);

            const response = await api.get(`/colaborador/comunicado/?codfun=${user.codfun}`);
            const data = [...response.data].sort((a, b) => dataEfetivaComunicado(b) - dataEfetivaComunicado(a));

            const naoLidos = data.filter(p => !p.viscom);
            const lidos = data.filter(p => p.viscom);
            setTodosLidos(lidos);

            const visiveis = [...naoLidos, ...lidos.slice(0, limiteAtual)];
            salvarCache(visiveis, lidos); // salva imediatamente, sem esperar counts
            setFeed(visiveis);
            if (!silencioso) setLoading(false);

        } catch (error) {
            if (!silencioso) {
                setLoading(false);
                alert("Erro ao conectar com servidor : " + error);
            }
        }
    }

    useEffect(() => {
        CarregarComunicados();
    }, []);

    function carregarMais() {
        const novoLimite = limiteLidos + LIMITE_LIDOS;
        setLimiteLidos(novoLimite);
        const naoLidos = feed.filter(p => !p.viscom);
        const visiveis = [...naoLidos, ...todosLidos.slice(0, novoLimite)];
        setFeed(visiveis);
        salvarCache(visiveis, todosLidos);
    }

    const temMais = todosLidos.length > limiteLidos;

    async function marcarComoVisualizado(id) {
        const post = feed.find(p => p.codcom === id);
        if (post?.viscom) return;
        api.post(`/colaborador/interacao/?tipfun=${user.tipfun}&codfun=${user.codfun}&tipint=viscom&codcom=${id}`)
            .catch(console.error);
        setFeed(prev => prev.map(p => p.codcom === id ? { ...p, viscom: true } : p));
        if (_cache.feed) {
            _cache.feed = _cache.feed.map(p => p.codcom === id ? { ...p, viscom: true } : p);
        }
    }

    async function toggleReaction(id, tipo) {
        await api.post(`/colaborador/interacao/?tipfun=${user.tipfun}&codfun=${user.codfun}&tipint=${tipo}&codcom=${id}`);
        setFeed(prev =>
            prev.map(post => {
                if (post.codcom !== id) return post;

                if (tipo === "curcom") {
                    const jaCurtiu = post.curcom > 0;
                    return { ...post, curcom: jaCurtiu ? 0 : 1, curtidas: jaCurtiu ? post.curtidas - 1 : post.curtidas + 1 };
                }
                if (tipo === "amecom") {
                    const jaAmou = post.amecom > 0;
                    return { ...post, amecom: jaAmou ? 0 : 1, ameis: jaAmou ? post.ameis - 1 : post.ameis + 1 };
                }
                return post;
            })
        );
    }

    async function addComment(id, texto) {
        if (!texto) return;
        setFeed(prev =>
            prev.map(post =>
                post.codcom === id
                    ? { ...post, mensagens: (post.mensagens || 0) + 1 }
                    : post
            )
        );
        await api.post(`/colaborador/comentario/?tipfun=${user.tipfun}&codfun=${user.codfun}&codcom=${id}&texto=${texto}`);
    }

    function saudacao() {
        const h = new Date().getHours();
        if (h < 12) return "Bom dia";
        if (h < 18) return "Boa tarde";
        return "Boa noite";
    }

    const refresh = async () => {
        limparCache();
        setLimiteLidos(LIMITE_LIDOS);
        await CarregarComunicados(LIMITE_LIDOS, true, true); // silencioso: sem skeleton
    };

    return (
        <>
            <PullToRefresh
                onRefresh={refresh}
                resistance={2.5}
                maxPullDownDistance={120}
                pullingContent={
                    <div className={styles.ptrContainer}>
                        <div className={styles.ptrIcon}>⬇️</div>
                        <div className={styles.ptrBar}>
                            <div className={styles.ptrProgressFake} />
                        </div>
                        <span>Puxe para atualizar</span>
                    </div>
                }
                refreshingContent={
                    <div className={styles.ptrContainer}>
                        <div className={styles.spinner} />
                        <span>Atualizando feed...</span>
                    </div>
                }
            >
                <div className={styles.feedContainer}>

                    {/* GREETING - rola junto com o feed, some ao tocar o header */}
                    <div
                        ref={greetingRef}
                        className={styles.greetingHeader}
                        style={{ opacity: greetingOpacity }}
                    >
                        <div className={styles.greetingAvatar}>
                            {foto
                                ? <img src={`data:image/jpeg;base64,${foto}`} alt="Foto" className={styles.greetingFoto} />
                                : user.nomfun?.charAt(0)
                            }
                        </div>
                        <div className={styles.greetingTexto}>
                            <h2>{saudacao()}, {user.nomfun?.split(' ')[0]}!</h2>
                            <p>{user.setor}</p>
                        </div>
                    </div>

                    <span className={styles.feedLabel}>📢 Comunicados</span>

                    {/* FEED */}
                    {feed.map((post) => (
                        <FeedCard
                            key={post.codcom}
                            post={post}
                            onLike={(id) => toggleReaction(id, "curcom")}
                            onLove={(id) => toggleReaction(id, "amecom")}
                            onView={marcarComoVisualizado}
                            onComment={addComment}
                        />
                    ))}

                    {/* Skeleton enquanto carrega */}
                    {loading && (
                        <>
                            <Skeleton />
                            <Skeleton />
                            <Skeleton />
                        </>
                    )}

                    {/* Carregar mais */}
                    {!loading && temMais && (
                        <button className={styles.btnCarregarMais} onClick={carregarMais}>
                            Carregar mais comunicados
                        </button>
                    )}

                    {/* Empty state */}
                    {!loading && feed.length === 0 && (
                        <div className={styles.emptyState}>
                            <div className={styles.emptyIlustration}>
                                <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="60" cy="60" r="54" fill="#fff1f2" />
                                    <rect x="32" y="38" width="56" height="44" rx="8" fill="#fecdd3" />
                                    <rect x="38" y="46" width="30" height="5" rx="2.5" fill="#e30613" opacity="0.5" />
                                    <rect x="38" y="56" width="44" height="4" rx="2" fill="#e30613" opacity="0.25" />
                                    <rect x="38" y="64" width="36" height="4" rx="2" fill="#e30613" opacity="0.25" />
                                    <rect x="38" y="72" width="22" height="4" rx="2" fill="#e30613" opacity="0.25" />
                                    <circle cx="85" cy="35" r="12" fill="#fff1f2" stroke="#fecdd3" strokeWidth="2" />
                                </svg>
                                <span className={styles.emptyEmoji}>😴</span>
                            </div>
                            <h3 className={styles.emptyTitulo}>Nenhum comunicado</h3>
                            <p className={styles.emptySubtitulo}>Puxe para baixo para atualizar ou volte mais tarde.</p>
                        </div>
                    )}

                    <div style={{ height: 40 }} />

                </div>
            </PullToRefresh>

            <BottomNav />
        </>
    );
}
